import MainTitle from "../../../components/ui/MainTitle/MainTitle";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useBlocker, useNavigate, useParams } from "react-router-dom";
import {
  useGetEmailTemplateByIdQuery,
  useGetEmailTemplateConstantsQuery,
  useGetEmailTemplateSubjectsQuery,
  useGetEmailTemplateVersionByLanguageQuery,
} from "../../../api/queries";
import {
  EmailTemplateLanguageType,
  EmailTemplateStatusEnum,
  ICreateOrUpdateEmailTemplate,
  IEmailTemplateVersion,
} from "../../../models/EmailTemplate";
import { Form, FormInstance, Modal } from "antd";
import FormItemSelect from "../../../components/ui/FormItemSelect/FormItemSelect";
import EmailBuilder, { getEmailBuilderHtml } from "../../../components/EmailBuilder/EmailBuilder";
import {
  createDefaultLayout,
  EmailBuilderValue,
  findConstants,
  parseLayout,
} from "../../../components/EmailBuilder/emailBuilder.model";
import { EmailTemplateService } from "../../../services/email-template.service";
import CustomButton from "../../../components/ui/CustomButton/CustomButton";
import { openErrorMessage, openSuccessMessage } from "../../../store/message/messageSlice";
import useAppDispatch from "../../../hooks/useAppDispatch";
import { useEmailTemplateMutations } from "../../../api/mutations/useEmailTemplateMutations";
import { handleError } from "../../../helper/errorHandler";
import { convertQuillToInlineStyles } from "../../../helper/convertQuillToInlineStyles";
import ConfirmPopup from "../../../components/ConfirmPopup/ConfirmPopup";

const languageSelectItems: { id: EmailTemplateLanguageType; label: string }[] = [
  { id: "en", label: "English" },
  { id: "el", label: "Greek" },
];

const CreateAutomatedEmail = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { id } = useParams();
  const formRef = useRef<FormInstance>(null);
  const { t, i18n } = useTranslation();
  const { createOrUpdateEmailTemplate } = useEmailTemplateMutations();

  const defaultLanguage = languageSelectItems[0].id;
  const [selectedLanguage, setSelectedLanguage] = useState(defaultLanguage);
  const [builderValue, setBuilderValue] = useState<EmailBuilderValue>({
    mode: "blocks",
    layout: createDefaultLayout(),
  });
  // Remounts the builder (a fresh undo history) when another version is loaded into it
  const [builderKey, setBuilderKey] = useState(0);
  const [subject, setSubject] = useState<string>();
  // The default layout nobody changed yet - rebuilt once the constants of the subject are known
  const isDefaultLayout = useRef(true);
  const [status, setStatus] = useState<EmailTemplateStatusEnum>();
  // What was loaded or last saved; the page has unsaved changes while the builder or the status differ from it
  const [saved, setSaved] = useState<{ builder: string; status?: EmailTemplateStatusEnum }>({
    builder: JSON.stringify(builderValue),
  });
  const hasUnsavedChanges =
    JSON.stringify(builderValue) !== saved.builder || status !== saved.status;
  // A language picked while there are unsaved changes - waits for the confirm
  const [pendingLanguage, setPendingLanguage] = useState<EmailTemplateLanguageType | null>(null);
  // Set right before leaving after a save, when the state isn't updated yet
  const isLeavingSaved = useRef(false);

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      hasUnsavedChanges &&
      !isLeavingSaved.current &&
      currentLocation.pathname !== nextLocation.pathname
  );

  // Reload or closing the tab: only the browser's own dialog can be shown there
  useEffect(() => {
    if (!hasUnsavedChanges) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [hasUnsavedChanges]);

  const { data: constants = [] } = useGetEmailTemplateConstantsQuery(subject);
  // Without a subject the server lists every constant - none of them is known to work yet
  const subjectConstants = useMemo(() => (subject ? constants : []), [subject, constants]);

  // isSaved - the version comes from the server as it is; a copy from English is a change still to save
  const loadIntoBuilder = (version: IEmailTemplateVersion | null, isSaved = true) => {
    const layout = parseLayout(version?.json);
    isDefaultLayout.current = !layout && !version?.content;
    let value: EmailBuilderValue;
    if (layout) value = { mode: "blocks", layout };
    // Saved before the builder, or HTML written by hand
    else if (version?.content)
      value = { mode: "html", html: convertQuillToInlineStyles(version.content) };
    else value = { mode: "blocks", layout: createDefaultLayout(subjectConstants) };
    setBuilderValue(value);
    if (isSaved) setSaved((prev) => ({ ...prev, builder: JSON.stringify(value) }));
    setBuilderKey((key) => key + 1);
  };

  useEffect(() => {
    if (!isDefaultLayout.current) return;
    const value: EmailBuilderValue = {
      mode: "blocks",
      layout: createDefaultLayout(subjectConstants),
    };
    setBuilderValue(value);
    setSaved((prev) => ({ ...prev, builder: JSON.stringify(value) }));
    setBuilderKey((key) => key + 1);
  }, [subjectConstants]);

  const onBuilderChange = (value: EmailBuilderValue) => {
    isDefaultLayout.current = false;
    setBuilderValue(value);
  };

  const { data: emailContractVersion = null, isError: isVersionMissing } =
    useGetEmailTemplateVersionByLanguageQuery(id!, selectedLanguage);
  const { data: subjects = [] } = useGetEmailTemplateSubjectsQuery();

  const subjectSelectItems = useMemo(() => {
    return subjects.map((el) => {
      const label = i18n.language === "en" ? el.labelEn : el.labelEl;
      return {
        id: el.id,
        label,
        disabled: el.alreadyInUse,
      };
    });
  }, [i18n.language, subjects]);
  const statusSelectItems = useMemo(() => {
    return [
      { id: EmailTemplateStatusEnum.Active, label: t("Active") },
      { id: EmailTemplateStatusEnum.Inactive, label: t("Inactive") },
      { id: EmailTemplateStatusEnum.Draft, label: t("Draft") },
    ];
  }, [t]);

  useEffect(() => {
    if (id !== "new" && emailContractVersion && formRef.current) {
      formRef.current.setFieldsValue({
        subject: emailContractVersion.emailTemplate.subject,
        status: emailContractVersion.emailTemplate.status,
        language: emailContractVersion.language,
      });
      setSelectedLanguage(emailContractVersion.language);
      setSubject(emailContractVersion.emailTemplate.subject);
      setStatus(emailContractVersion.emailTemplate.status);
      setSaved((prev) => ({ ...prev, status: emailContractVersion.emailTemplate.status }));
      loadIntoBuilder(emailContractVersion);
    }
  }, [emailContractVersion, id]);

  // The template has no version in this language yet - start from the default layout
  useEffect(() => {
    if (id !== "new" && isVersionMissing) loadIntoBuilder(null);
  }, [isVersionMissing, selectedLanguage, id]);

  // Either language can be written first: the other one starts as its copy
  const { data: emailTemplate } = useGetEmailTemplateByIdQuery(id !== "new" ? id! : "");
  const copySource: EmailTemplateLanguageType = selectedLanguage === "en" ? "el" : "en";
  const canCopy = !!emailTemplate?.emailTemplateVersions.some((v) => v.language === copySource);
  const copyTexts =
    copySource === "en"
      ? { button: t("CopyFromEnglish"), confirm: t("CopyFromEnglishConfirm") }
      : { button: t("CopyFromGreek"), confirm: t("CopyFromGreekConfirm") };

  const copyFromOtherLanguage = () => {
    Modal.confirm({
      title: copyTexts.button,
      content: copyTexts.confirm,
      okText: t("Confirm"),
      cancelText: t("Cancel"),
      onOk: async () => {
        try {
          const res = await EmailTemplateService.getEmailTemplateVersionByLanguage(id!, copySource);
          loadIntoBuilder(res.data, false);
        } catch (e) {
          handleError(e, dispatch, t);
        }
      },
    });
  };

  const onFinish = async (values: Omit<ICreateOrUpdateEmailTemplate, "content" | "json">) => {
    const html = getEmailBuilderHtml(builderValue);
    if (!html.trim()) {
      dispatch(openErrorMessage(t("ContentCanNotBeEmpty")));
      return;
    }
    // The server would send them as empty text
    const unsupported = findConstants(html).filter((c) => !subjectConstants.includes(c));
    if (unsupported.length) {
      dispatch(openErrorMessage(t("UnsupportedConstants", { constants: unsupported.join(", ") })));
      return;
    }

    const model: ICreateOrUpdateEmailTemplate = {
      ...values,
      content: builderValue.mode === "html" ? convertQuillToInlineStyles(html) : html,
      json: builderValue.mode === "blocks" ? JSON.stringify(builderValue.layout) : null,
    };

    createOrUpdateEmailTemplate(model, {
      onSuccess: () => {
        dispatch(openSuccessMessage(t("EmailTemplateSavedSuccessfully")));
        isLeavingSaved.current = true;
        navigate("/automated-emails");
      },
      onError: (e) => handleError(e, dispatch, t),
    });
  };

  return (
    <div className={"page-content"}>
      <MainTitle text={t("AutomatedEmails")} />

      <Form
        className={"form"}
        ref={formRef}
        name="create-email-template"
        onFinish={onFinish}
        autoComplete="off"
        layout={"vertical"}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "24px" }}>
          <FormItemSelect
            name={"subject"}
            label={t("EmailSubject")}
            rules={{ required: true }}
            items={subjectSelectItems}
            disabled={!!emailContractVersion}
            onChange={(value) => {
              setSubject(value as string);
              // A new email starts in English and active; picking them is not a change to save
              const form = formRef.current;
              if (form && !form.getFieldValue("language")) {
                form.setFieldsValue({ language: defaultLanguage });
                setSelectedLanguage(defaultLanguage);
              }
              if (form && form.getFieldValue("status") === undefined) {
                form.setFieldsValue({ status: EmailTemplateStatusEnum.Active });
                setStatus(EmailTemplateStatusEnum.Active);
                setSaved((prev) => ({ ...prev, status: EmailTemplateStatusEnum.Active }));
              }
            }}
          />

          <FormItemSelect
            name={"language"}
            label={t("Language")}
            rules={{ required: true }}
            items={languageSelectItems}
            onChange={(value) => {
              const typeValue = value as EmailTemplateLanguageType;
              // The other language loads its own version, so the changes here would be lost
              if (hasUnsavedChanges) {
                formRef.current?.setFieldsValue({ language: selectedLanguage });
                setPendingLanguage(typeValue);
                return;
              }
              setSelectedLanguage(typeValue);
            }}
          />

          <FormItemSelect
            name={"status"}
            label={t("Status")}
            rules={{ required: true }}
            items={statusSelectItems}
            onChange={(value) => setStatus(value as EmailTemplateStatusEnum)}
          />
        </div>

        {canCopy && (
          <CustomButton
            text={copyTexts.button}
            type={"button"}
            typeStyle={"outline"}
            style={{ width: "auto", marginBottom: "16px" }}
            functionOnClick={copyFromOtherLanguage}
          />
        )}

        {/* A new email: the layout and the constants depend on the subject */}
        {id !== "new" || subject ? (
          <>
            <EmailBuilder
              key={builderKey}
              value={builderValue}
              onChange={onBuilderChange}
              language={selectedLanguage}
              constants={subjectConstants}
            />

            <CustomButton
              text={t("SaveChanges")}
              type={"button"}
              typeStyle={"fill"}
              style={{ width: "400px", marginTop: "24px" }}
              functionOnClick={() => formRef.current?.submit()}
            />
          </>
        ) : (
          <p style={{ color: "#939393" }}>{t("ChooseEmailSubjectToStart")}</p>
        )}
      </Form>

      {blocker.state === "blocked" && (
        <ConfirmPopup
          title={t("UnsavedChangesLeavePage")}
          onClickYes={() => blocker.proceed()}
          onClickNo={() => blocker.reset()}
        />
      )}

      {pendingLanguage && (
        <ConfirmPopup
          title={t("UnsavedChangesLeavePage")}
          onClickYes={() => {
            formRef.current?.setFieldsValue({ language: pendingLanguage });
            setSelectedLanguage(pendingLanguage);
            setPendingLanguage(null);
          }}
          onClickNo={() => setPendingLanguage(null)}
        />
      )}
    </div>
  );
};

export default CreateAutomatedEmail;
